/**
 * Appearance (D19 phase 2; ADR-0036): the theme, the style and the transparency, chosen by the person, never inherited
 * from the OS, kept in this browser. Each is an attribute on <html>: data-theme, data-style, data-glass.
 *
 *   · Theme: the approved RTCOM look ('light', the default) and its dark pair, and twelve named themes (themes.css).
 *   · Style: classic — ADR-0011's flat look, the default — or glass: the art canvas, the glass sidebar and top bar
 *     joined by an inner curve, raised panels (appearance.css).
 *   · Transparency: full, reduced or off (for accessibility and slow machines; it follows the OS where it can).
 */
import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { Dialog } from '../ui/overlays';
import { Button, cx } from '../ui/primitives';

export interface ThemeDef { id: string; label: string; mode: 'light' | 'dark'; swatch: [string, string, string, string] }
export const THEMES: readonly ThemeDef[] = [
  { id: 'desigo', label: 'Desigo', mode: 'light', swatch: ['#F0F1F1', '#FFFFFF', '#005FCC', '#1E1F21'] },
  { id: 'light', label: 'RTCOM', mode: 'light', swatch: ['#f5f7fb', '#ffffff', '#0a2a66', '#0e1a2b'] },
  { id: 'dark', label: 'RTCOM Dark', mode: 'dark', swatch: ['#0e1116', '#161b23', '#8db1f4', '#ecf1f9'] },
  { id: 'jodhpur-blue', label: 'Jodhpur Blue', mode: 'light', swatch: ['#F3F6FB', '#3B6FC4', '#1F4E9A', '#E0A458'] },
  { id: 'thar-sand', label: 'Thar Sand', mode: 'light', swatch: ['#F6EFE3', '#D9B98A', '#9A5B1E', '#C1683C'] },
  { id: 'terracotta', label: 'Terracotta', mode: 'light', swatch: ['#F7EEE8', '#E35336', '#A8462B', '#8C9A6B'] },
  { id: 'marigold', label: 'Marigold', mode: 'light', swatch: ['#FBF4E4', '#F2A516', '#8A5A00', '#E06A2C'] },
  { id: 'sage', label: 'Sage', mode: 'light', swatch: ['#F1F4EE', '#BBB791', '#4F6B4A', '#DCA1A1'] },
  { id: 'rose-clay', label: 'Rose Clay', mode: 'light', swatch: ['#F8EFEF', '#DCA1A1', '#A34D5E', '#C97B63'] },
  { id: 'monsoon', label: 'Monsoon', mode: 'light', swatch: ['#EEF3F4', '#7FA7AE', '#2D6A73', '#4A6670'] },
  { id: 'ivory', label: 'Ivory', mode: 'light', swatch: ['#FAF7F0', '#E8DCC2', '#7A5C2E', '#C9B48A'] },
  { id: 'peacock', label: 'Peacock', mode: 'light', swatch: ['#EDF5F4', '#1B8A8A', '#0E6E6E', '#C7A23C'] },
  { id: 'indigo-night', label: 'Indigo Night', mode: 'dark', swatch: ['#12142B', '#5A4FB0', '#8EA2FF', '#E0A458'] },
  { id: 'desert-dusk', label: 'Desert Dusk', mode: 'dark', swatch: ['#221A24', '#6B3A4E', '#E89A6B', '#C1683C'] },
  { id: 'charcoal-glass', label: 'Charcoal Glass', mode: 'dark', swatch: ['#121316', '#4A4E5A', '#E8B04B', '#8A6A2C'] },
];
export type Style = 'classic' | 'glass';
export type Transparency = 'full' | 'reduced' | 'off';
export type Canvas = 'motifs' | 'pictures';
export type Density = 'compact' | 'comfortable';
export type Motion = 'full' | 'reduced';
export interface Appearance { theme: string; style: Style; glass: Transparency; canvas: Canvas; density: Density; motion: Motion }

const read = (key: string, fallback: string) => { try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; } };
const write = (key: string, value: string) => { try { localStorage.setItem(key, value); } catch { /* storage blocked: it resets next time */ } };

function readAll(): Appearance {
  // The owner, 2026-10-18 (CRM brief): Desigo, from his Figma palette, is the look the desk opens with.
  const theme = read('rtcom.theme', 'desigo');
  return { theme: THEMES.some((t) => t.id === theme) ? theme : 'desigo',
           style: read('rtcom.style', 'classic') === 'glass' ? 'glass' : 'classic',
           glass: (['full', 'reduced', 'off'].includes(read('rtcom.glass', 'full')) ? read('rtcom.glass', 'full') : 'full') as Transparency,
           canvas: read('rtcom.canvas', 'motifs') === 'pictures' ? 'pictures' : 'motifs',
           density: read('rtcom.density', 'compact') === 'comfortable' ? 'comfortable' : 'compact',
           motion: read('rtcom.motion', 'full') === 'reduced' ? 'reduced' : 'full' };
}
const EVENT = 'rtcom:appearance';

/** The appearance, kept in this browser. Every place that shows the setting (the top bar's dialog, Settings) reads
 *  and writes the same values, and hears the others' changes (D19 phase 17). */
export function useAppearance(): [Appearance, (next: Partial<Appearance>) => void] {
  const [a, setA] = useState<Appearance>(readAll);
  useEffect(() => {
    const on = () => setA(readAll());
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = a.theme;
    root.dataset.style = a.style;
    root.dataset.glass = a.glass;
    root.dataset.canvas = a.canvas;
    root.dataset.density = a.density;
    root.dataset.motion = a.motion;
    root.style.colorScheme = THEMES.find((t) => t.id === a.theme)?.mode ?? 'light';
    write('rtcom.theme', a.theme); write('rtcom.style', a.style); write('rtcom.glass', a.glass); write('rtcom.canvas', a.canvas);
    write('rtcom.density', a.density); write('rtcom.motion', a.motion);
  }, [a]);
  return [a, (next) => setA((cur) => {
    const merged = { ...cur, ...next };
    (Object.keys(next) as (keyof Appearance)[]).forEach((k) => write(`rtcom.${k === 'glass' ? 'glass' : k}`, String(merged[k])));
    queueMicrotask(() => window.dispatchEvent(new Event(EVENT)));
    return merged;
  })];
}

export const isDark = (a: Appearance) => THEMES.find((t) => t.id === a.theme)?.mode === 'dark';

export function AppearanceDialog({ open, onClose, value, onChange }: { open: boolean; onClose: () => void; value: Appearance; onChange: (next: Partial<Appearance>) => void }) {
  return (
    <Dialog open={open} onClose={onClose} title="Appearance" width="44rem" footer={<div className="flex justify-end"><Button variant="primary" onClick={onClose}>Done</Button></div>}>
      <AppearanceControls value={value} onChange={onChange} />
    </Dialog>
  );
}

/** Settings › Appearance (D19 phase 17): the same controls on a page of their own. */
export function AppearancePanel() {
  const [value, onChange] = useAppearance();
  return (
    <section className="panel mb-4 rounded-[var(--radius-panel)] border border-line bg-surface p-4">
      <h2 className="text-section mb-3">Appearance <span className="text-small font-normal text-ink-muted">· for you, in this browser</span></h2>
      <AppearanceControls value={value} onChange={onChange} />
    </section>
  );
}

export function AppearanceControls({ value, onChange }: { value: Appearance; onChange: (next: Partial<Appearance>) => void }) {
  const picturesReady = usePicturesReady();
  return (
      <div className="grid gap-5">
        <section>
          <h3 className="label-text mb-2">Style</h3>
          <div className="grid gap-2 sm:grid-cols-2">
            {([['classic', 'Classic', 'The approved RTCOM look: flat, hairlines, small corners.'],
               ['glass', 'Glass', 'A Rajasthani canvas, a glass sidebar and top bar joined by a curve, raised rounded panels.']] as const).map(([id, label, note]) => (
              <button key={id} type="button" onClick={() => onChange({ style: id })} aria-pressed={value.style === id}
                className={cx('rounded-[var(--radius-control)] border px-3 py-2 text-left', value.style === id ? 'border-brand bg-brand-soft' : 'border-line hover:bg-surface-sunken')}>
                <div className="flex items-center gap-2 font-medium">{value.style === id && <Check size={14} />}{label}</div>
                <div className="text-small text-ink-muted">{note}</div>
              </button>))}
          </div>
        </section>
        <section>
          <h3 className="label-text mb-2">Theme</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {THEMES.map((t) => (
              <button key={t.id} type="button" onClick={() => onChange({ theme: t.id })} aria-pressed={value.theme === t.id}
                className={cx('rounded-[var(--radius-control)] border p-2 text-left', value.theme === t.id ? 'border-brand ring-2 ring-brand' : 'border-line hover:bg-surface-sunken')}>
                <div className="mb-1.5 flex h-8 overflow-hidden rounded-[var(--radius-badge)]" aria-hidden>
                  {t.swatch.map((c, i) => <span key={i} className="flex-1" style={{ background: c }} />)}
                </div>
                <div className="flex items-center gap-1 text-small font-medium">{value.theme === t.id && <Check size={12} />}{t.label}</div>
                <div className="text-[11px] text-ink-muted">{t.mode}</div>
              </button>))}
          </div>
        </section>
        <section className={cx(value.style !== 'glass' && 'opacity-50')}>
          <h3 className="label-text mb-2">Background (glass style)</h3>
          <div className="grid gap-2 sm:grid-cols-2">
            {([['motifs', 'Rajasthani motifs', 'The theme\u2019s pattern (bandhani, buta, paisley, dunes) and a jharokha, mandala or peacock feather.'],
               ['pictures', 'Rajasthani pictures', picturesReady ? 'A painted Rajasthani scene for each workspace, by day, or by night in a dark theme.' : 'A painted scene for each workspace \u2014 arrives when the pictures are added (public/art/rajasthan).']] as const).map(([id, label, note]) => (
              <button key={id} type="button" disabled={value.style !== 'glass' || (id === 'pictures' && !picturesReady)} onClick={() => onChange({ canvas: id })} aria-pressed={value.canvas === id}
                className={cx('rounded-[var(--radius-control)] border px-3 py-2 text-left', value.canvas === id ? 'border-brand bg-brand-soft' : 'border-line hover:bg-surface-sunken')}>
                <div className="flex items-center gap-2 font-medium">{value.canvas === id && <Check size={14} />}{label}</div>
                <div className="text-small text-ink-muted">{note}</div>
              </button>))}
          </div>
        </section>
        <section className={cx(value.style !== 'glass' && 'opacity-50')}>
          <h3 className="label-text mb-2">Transparency (glass style)</h3>
          <div className="flex gap-2">
            {(['full', 'reduced', 'off'] as const).map((g) => (
              <Button key={g} size="dense" variant={value.glass === g ? 'primary' : 'secondary'} disabled={value.style !== 'glass'} onClick={() => onChange({ glass: g })}>{g}</Button>))}
          </div>
          <p className="mt-1 text-small text-ink-muted">Reduce or turn it off if the screen feels busy or the computer is slow. Status still shows a colour, an icon and a word in every theme.</p>
        </section>
        <section>
          <h3 className="label-text mb-2">Density and motion</h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex gap-2">{(['compact', 'comfortable'] as const).map((d) => (
              <Button key={d} size="dense" variant={value.density === d ? 'primary' : 'secondary'} onClick={() => onChange({ density: d })}>{d === 'compact' ? 'Compact rows' : 'Comfortable rows'}</Button>))}</div>
            <div className="flex gap-2">{(['full', 'reduced'] as const).map((m) => (
              <Button key={m} size="dense" variant={value.motion === m ? 'primary' : 'secondary'} onClick={() => onChange({ motion: m })}>{m === 'full' ? 'Motion on' : 'Reduce motion'}</Button>))}</div>
          </div>
          <p className="mt-1 text-small text-ink-muted">Comfortable gives every table row more room; reduce motion stops the lifts, the shimmer and the page transitions.</p>
        </section>
      </div>
  );
}

/** Whether the Rajasthani pictures have been added (public/art/rajasthan): the Home picture stands for the set. */
function usePicturesReady() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let live = true;
    fetch('/art/rajasthan/home-light.webp', { method: 'HEAD' })
      .then((r) => { if (live) setReady(r.ok && (r.headers.get('content-type') ?? '').startsWith('image/')); })
      .catch(() => undefined);
    return () => { live = false; };
  }, []);
  return ready;
}
